import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../ClientManagement.module.css";
import { clientService } from "../../services/clientService";
import { clientTypeService } from "../../services/clientTypeService";
import { clientTypeTagsService } from "../../services/clientTypeTagsService";
import { useAccessToken } from "../../hooks/useAuth";
import { useAlert } from "../../context/AlertContext";

const AddClient = () => {
    const navigate = useNavigate();
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert, setInfoAlert } = useAlert();

    // Form state
    const [formData, setFormData] = useState({
        clientCode: '',
        clientName: '',
        isActive: true
    });

    // UI state
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [similarClients, setSimilarClients] = useState([]);
    const [isCheckingSimilarity, setIsCheckingSimilarity] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Client type tags state
    const [availableClientTypes, setAvailableClientTypes] = useState([]);
    const [selectedClientTypes, setSelectedClientTypes] = useState([]);
    const [clientTypesLoading, setClientTypesLoading] = useState(true);

    /**
     * Load available client types on component mount
     */
    useEffect(() => {
        loadAvailableClientTypes();
        
        // Cleanup function to cancel any pending similarity checks
        return () => {
            setSimilarClients([]);
            setIsCheckingSimilarity(false);
        };
    }, []);

    const loadAvailableClientTypes = async () => {
        try {
            setClientTypesLoading(true);
            const accessToken = await getBackendAccessToken();
            const types = await clientTypeService.getAll(accessToken);
            setAvailableClientTypes(types || []);
        } catch (error) {
            console.error('Failed to load client types:', error);
            setErrorAlert('Failed to load client types. Tag selection may not be available.');
            setAvailableClientTypes([]);
        } finally {
            setClientTypesLoading(false);
        }
    };

    /**
     * Handle client type selection
     */
    const handleClientTypeToggle = (clientTypeId) => {
        setSelectedClientTypes(prev => {
            if (prev.includes(clientTypeId)) {
                return prev.filter(id => id !== clientTypeId);
            } else {
                return [...prev, clientTypeId];
            }
        });
    };

    /**
     * Handle input changes
     */
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear field-specific errors when user starts typing
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }

        // Check for similar names when client name changes
        if (field === 'clientName') {
            // Clear similar clients if name is empty or too short
            if (!value || value.trim().length <= 2) {
                setSimilarClients([]);
                setIsCheckingSimilarity(false);
            } else {
                debouncedSimilarityCheck(value);
            }
        }
    };

    /**
     * Handle client name changes and check for similar names
     */
    const handleClientNameChange = useCallback(async (value) => {
        // Clear existing similar clients if name is too short
        if (value.trim().length <= 2) {
            setSimilarClients([]);
            return;
        }

        try {
            setIsCheckingSimilarity(true);
            const accessToken = await getBackendAccessToken();
            const similar = await clientService.findSimilarNames(accessToken, value.trim(), 0.6);
            
            // Show all similar clients including exact matches (potential duplicates)
            setSimilarClients(similar || []);
        } catch (error) {
            console.error('Error checking for similar client names:', error);
            setSimilarClients([]);
        } finally {
            setIsCheckingSimilarity(false);
        }
    }, [getBackendAccessToken]);

    // Debounce the similarity check to avoid excessive API calls
    const debouncedSimilarityCheck = useCallback(
        (() => {
            let timeoutId;
            return (value) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => handleClientNameChange(value), 500);
            };
        })(),
        [handleClientNameChange]
    );

    /**
     * Validate client code uniqueness in real-time
     */
    const handleClientCodeBlur = useCallback(async () => {
        const clientCode = formData.clientCode.trim();
        
        if (!clientCode) return;

        try {
            setIsValidating(true);
            const accessToken = await getBackendAccessToken();
            const exists = await clientService.checkCodeExists(accessToken, clientCode);
            
            if (exists) {
                setErrors(prev => ({
                    ...prev,
                    clientCode: 'Client code already exists'
                }));
            }
        } catch (error) {
            console.error('Error checking client code uniqueness:', error);
            // Don't show error to user for this validation check
        } finally {
            setIsValidating(false);
        }
    }, [formData.clientCode, getBackendAccessToken]);

    /**
     * Validate form data
     */
    const validateForm = () => {
        const newErrors = {};

        // Check that at least one field is provided
        if (!formData.clientCode.trim() && !formData.clientName.trim()) {
            newErrors.general = 'Either client code or client name must be provided';
        }

        // Validate client code
        if (formData.clientCode.trim()) {
            if (formData.clientCode.length > 6) {
                newErrors.clientCode = 'Client code cannot exceed 6 characters';
            }
        }

        // Validate client name
        if (formData.clientName.trim()) {
            if (formData.clientName.length > 50) {
                newErrors.clientName = 'Client name cannot exceed 50 characters';
            }
        }

        return newErrors;
    };

    /**
     * Handle form submission
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        const validationErrors = validateForm();
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        // Check if there are similar clients and show confirmation dialog
        if (similarClients.length > 0 && !showConfirmDialog) {
            setShowConfirmDialog(true);
            return;
        }

        // Proceed with client creation
        await createClient();
    };

    /**
     * Create the client after validation and confirmation
     */
    const createClient = async () => {
        try {
            setLoading(true);
            setShowConfirmDialog(false);
            const accessToken = await getBackendAccessToken();
            
            // Prepare data for API
            const clientData = {};
            if (formData.clientCode.trim()) {
                clientData.clientCode = formData.clientCode.trim();
            }
            if (formData.clientName.trim()) {
                clientData.clientName = formData.clientName.trim();
            }
            clientData.isActive = formData.isActive;

            // Create the client first
            const createdClient = await clientService.create(accessToken, clientData);
            
            // Assign selected client type tags if any are selected
            if (selectedClientTypes.length > 0 && createdClient.clientId) {
                try {
                    await clientTypeTagsService.assignClientTypesToClient(
                        accessToken, 
                        createdClient.clientId, 
                        selectedClientTypes
                    );
                    
                    if (selectedClientTypes.length === 1) {
                        setInfoAlert('Client created successfully with 1 client type tag assigned');
                    } else {
                        setInfoAlert(`Client created successfully with ${selectedClientTypes.length} client type tags assigned`);
                    }
                } catch (tagError) {
                    console.error('Failed to assign client type tags:', tagError);
                    setErrorAlert('Client created successfully, but failed to assign some client type tags. You can assign them manually later.');
                }
            } else {
                setInfoAlert('Client created successfully');
            }
            
            navigate('/clients/list');
        } catch (error) {
            console.error('Failed to create client:', error);
            setErrorAlert('Failed to create client. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.addClientSection}>
            <h2>Add New Client</h2>
            <div className={styles.formContainer}>
                <form className={styles.clientForm} onSubmit={handleSubmit}>
                    {/* General Error Message */}
                    {errors.general && (
                        <div className={styles.errorMessage}>
                            {errors.general}
                        </div>
                    )}

                    {/* Basic Client Information */}
                    <div className={styles.formSection}>
                        <h3>Client Information</h3>
                        <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                                <label htmlFor="clientCode">
                                    Client Code 
                                    <span className={styles.optional}>(Max 6 chars)</span>
                                </label>
                                <input
                                    id="clientCode"
                                    type="text"
                                    placeholder="e.g., ABC123"
                                    value={formData.clientCode}
                                    onChange={(e) => handleInputChange('clientCode', e.target.value.toUpperCase())}
                                    onBlur={handleClientCodeBlur}
                                    maxLength={6}
                                    disabled={loading}
                                />
                                <div className={styles.counter}>
                                    {formData.clientCode.length}/6 characters
                                </div>
                                {errors.clientCode && (
                                    <div className={styles.errorMessage}>
                                        {errors.clientCode}
                                    </div>
                                )}
                                {isValidating && (
                                    <div className={styles.validatingMessage}>
                                        Checking availability...
                                    </div>
                                )}
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor="clientName">
                                    Client Name 
                                    <span className={styles.optional}>(Optional, max 50 chars)</span>
                                </label>
                                <input
                                    id="clientName"
                                    type="text"
                                    placeholder="e.g., Acme Corporation"
                                    value={formData.clientName}
                                    onChange={(e) => handleInputChange('clientName', e.target.value)}
                                    maxLength={50}
                                    disabled={loading}
                                />
                                <div className={styles.counter}>
                                    {formData.clientName.length}/50 characters
                                </div>
                                {errors.clientName && (
                                    <div className={styles.errorMessage}>
                                        {errors.clientName}
                                    </div>
                                )}
                                {isCheckingSimilarity && (
                                    <div className={styles.validatingMessage}>
                                        Checking for similar clients...
                                    </div>
                                )}
                                {similarClients.length > 0 && (() => {
                                    const exactMatches = similarClients.filter(client => 
                                        client.clientName && 
                                        client.clientName.toLowerCase() === formData.clientName.trim().toLowerCase()
                                    );
                                    const similarMatches = similarClients.filter(client => 
                                        client.clientName && 
                                        client.clientName.toLowerCase() !== formData.clientName.trim().toLowerCase()
                                    );
                                    
                                    return (
                                        <div className={styles.warningMessage}>
                                            {exactMatches.length > 0 && (
                                                <>
                                                    <strong>🚨 Exact Match Found:</strong> A client with this exact name already exists:
                                                    <div className={styles.similarClientsList}>
                                                        <ul>
                                                            {exactMatches.map(client => (
                                                                <li key={client.clientId} style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                                                                    <span className={styles.clientInfo}>
                                                                        {client.clientCode ? `${client.clientCode} - ` : ''}{client.clientName}
                                                                    </span>
                                                                    {!client.isActive && (
                                                                        <span className={styles.inactiveClient}> (Inactive)</span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </>
                                            )}
                                            {similarMatches.length > 0 && (
                                                <>
                                                    {exactMatches.length > 0 && <br />}
                                                    <strong>⚠️ Similar Clients Found:</strong>
                                                    <div className={styles.similarClientsList}>
                                                        <ul>
                                                            {similarMatches.map(client => (
                                                                <li key={client.clientId}>
                                                                    <span className={styles.clientInfo}>
                                                                        {client.clientCode ? `${client.clientCode} - ` : ''}{client.clientName}
                                                                    </span>
                                                                    {!client.isActive && (
                                                                        <span className={styles.inactiveClient}> (Inactive)</span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </>
                                            )}
                                            <div className={styles.warningNote}>
                                                {exactMatches.length > 0 
                                                    ? 'This appears to be a duplicate client. Please verify before proceeding.'
                                                    : 'Please verify this is not a duplicate before proceeding.'
                                                }
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={formData.isActive}
                                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                                    disabled={loading}
                                />
                                <span>Client is active</span>
                            </label>
                        </div>
                    </div>

                    {/* Client Type Tags Section - Condensed */}
                    <div className={styles.formSection}>
                        <h3>Client Type Tags</h3>
                        
                        {clientTypesLoading ? (
                            <div className={styles.loadingMessage}>
                                <div className={styles.spinner}></div>
                                Loading client types...
                            </div>
                        ) : availableClientTypes.length === 0 ? (
                            <div className={styles.emptyMessage}>
                                No client types available.
                            </div>
                        ) : (
                            <div className={styles.clientTypeSelector}>
                                <div className={styles.compactClientTypeGrid}>
                                    {availableClientTypes.map(clientType => (
                                        <label 
                                            key={clientType.clientTypeId} 
                                            className={`${styles.compactClientTypeOption} ${
                                                selectedClientTypes.includes(clientType.clientTypeId) ? styles.selected : ''
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedClientTypes.includes(clientType.clientTypeId)}
                                                onChange={() => handleClientTypeToggle(clientType.clientTypeId)}
                                                disabled={loading}
                                                className={styles.compactCheckbox}
                                            />
                                            <span className={styles.compactClientTypeName}>
                                                {clientType.clientType}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                                
                                {selectedClientTypes.length > 0 && (
                                    <div className={styles.compactSelectedInfo}>
                                        {selectedClientTypes.length} selected
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className={styles.formActions}>
                        <button 
                            type="button" 
                            className={styles.cancelButton} 
                            onClick={() => navigate('/clients')}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className={styles.submitButton}
                            disabled={loading || isValidating || isCheckingSimilarity || Object.keys(errors).length > 0}
                        >
                            {loading ? 'Creating...' : 'Create Client'}
                        </button>
                    </div>

                  
                </form>
            </div>

            {/* Confirmation Dialog for Similar Clients */}
            {showConfirmDialog && (() => {
                const exactMatches = similarClients.filter(client => 
                    client.clientName && 
                    client.clientName.toLowerCase() === formData.clientName.trim().toLowerCase()
                );
                const similarMatches = similarClients.filter(client => 
                    client.clientName && 
                    client.clientName.toLowerCase() !== formData.clientName.trim().toLowerCase()
                );
                
                return (
                    <div className={styles.modal}>
                        <div className={styles.modalContent}>
                            <h3>{exactMatches.length > 0 ? '🚨 Duplicate Client Warning' : '⚠️ Potential Duplicate Client'}</h3>
                            
                            {exactMatches.length > 0 && (
                                <>
                                    <p><strong>An exact match was found:</strong></p>
                                    <div className={styles.similarClientsList}>
                                        <ul>
                                            {exactMatches.map(client => (
                                                <li key={client.clientId} style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                                                    {client.clientCode ? `${client.clientCode} - ` : ''}{client.clientName}
                                                    {!client.isActive && (
                                                        <span className={styles.inactiveClient}> (Inactive)</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </>
                            )}
                            
                            {similarMatches.length > 0 && (
                                <>
                                    {exactMatches.length > 0 && <br />}
                                    <p><strong>Similar clients were also found:</strong></p>
                                    <div className={styles.similarClientsList}>
                                        <ul>
                                            {similarMatches.map(client => (
                                                <li key={client.clientId}>
                                                    <strong>
                                                        {client.clientCode ? `${client.clientCode} - ` : ''}{client.clientName}
                                                    </strong>
                                                    {!client.isActive && (
                                                        <span className={styles.inactiveClient}> (Inactive)</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </>
                            )}
                            
                            <p>
                                {exactMatches.length > 0 
                                    ? 'This appears to be a duplicate. Are you sure you want to create this client anyway?'
                                    : 'Are you sure you want to create this new client?'
                                }
                            </p>
                            <div className={styles.formActions}>
                                <button 
                                    type="button" 
                                    className={styles.cancelButton}
                                    onClick={() => setShowConfirmDialog(false)}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className={styles.submitButton}
                                    onClick={createClient}
                                    disabled={loading}
                                >
                                    {loading ? 'Creating...' : 'Yes, Create Client'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default AddClient;
